import { CHAPTER_THEMES } from './chapterThemes';
import { DEMO_FERTILITY_IVF_TIMELINE, formatIvfTimelineCallout } from '../utils/ivfCycleTimeline';

export const CHAPTER_DISPLAY_NAMES = {
  fertility: 'Fertility',
  ttc: 'Trying to Conceive',
  pregnancy: 'Pregnancy',
  motherhood: 'Motherhood',
  menopause: 'Menopause',
  firstchapter: 'First Chapter',
};

/** Fertility demo shell pill — cycle · stim day · days to retrieval (matches IVF timeline). */
export const FERTILITY_DEMO_STATUS_PILL_TEXT = formatIvfTimelineCallout(DEMO_FERTILITY_IVF_TIMELINE);

export const PAGE_HEADERS = {
  'fertility/home': {
    pageEyebrow: 'Fertility · Home',
    pageTitle: 'Your fertility hub',
    pageDescriptor: 'Your IVF cycle, Nova check-ins, and shortcuts to every chapter tool.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'MAYA · CYCLE 2 · STIM DAYS 1–8',
      title: 'Maya, your cycle is right where we want it across stim days 1–8.',
      subtitle: 'Monitoring tomorrow morning at Ohio Fertility Network · Antagonist protocol with Dr. Patel · ~9 days to expected retrieval',
      pills: [
        { label: 'Stim days 1–8', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Scan tomorrow · Columbus, OH' },
        { label: 'Cycle 2 · Antagonist' },
      ],
    },
    novaMessage: {
      context: 'daily check-in',
      message:
        "Maya — stim days 1–8 of this cycle. I'll have your monitoring questions ready for tomorrow at Ohio Fertility Network in Columbus, and tonight's injection reminders at 9pm.",
    },
  },
  'fertility/myjourney': {
    pageEyebrow: 'Fertility · My Journey',
    pageTitle: 'Your IVF story',
    pageDescriptor: 'Cycles, stims, and milestones — Nova uses this to personalize your support.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'IVF · CYCLE 2 · STIM DAYS 1–8',
      title: 'Days 1–8 of stimulation.',
      subtitle: 'Mar 20, 2026 · Antagonist · Ohio Fertility Network',
      pills: [
        { label: 'Retrieval ~9 days away', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Injections at 9pm tonight' },
        { label: '12 follicles last scan' },
      ],
      stat: { value: '~9', label: 'days to retrieval' },
      statCallout: formatIvfTimelineCallout(DEMO_FERTILITY_IVF_TIMELINE),
    },
    novaMessage: {
      context: 'morning check-in',
      message: "How are you feeling today? Your injections are at 9pm tonight — set a reminder if you haven't.",
    },
  },
  'fertility/health': {
    pageEyebrow: 'Fertility · Health Record',
    pageTitle: 'Your health record',
    pageDescriptor: 'Labs, clinic notes, and cycle history — all in one editable record.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'HEALTH RECORD · FERTILITY · CYCLE 2',
      title: 'Your health at a glance.',
      subtitle: 'Labs, clinic info, and cycle history — edit any section below',
      pills: [
        { label: 'AMH 2.1 · FSH 7.2', dot: true, dotColor: 'bg-teal-400' },
        { label: 'AFC 12 · high normal' },
        { label: 'Cycle 2 baseline' },
      ],
    },
    novaMessage: {
      context: 'on your labs',
      message:
        'Your AMH of 2.1 and FSH of 7.2 are both solid heading into stims. AFC at 12 is on the high end of normal — worth watching estradiol closely.',
    },
    hideNovaMessage: true,
  },
  'fertility/financial': {
    pageEyebrow: 'Fertility · Financial Tracker',
    pageTitle: "What you've spent",
    pageDescriptor: 'IVF costs tracked cycle by cycle — medications, monitoring, and more.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'FINANCIAL TRACKER · IVF CYCLE 2',
      title: 'Your IVF costs.',
      subtitle: 'Track spending, upload receipts, compare to city and national averages',
      pills: [
        { label: '13% of cycle spent', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Retrieval cost coming' },
        { label: '$3,765 of $28,500 est.' },
      ],
    },
    novaMessage: {
      context: 'cost alert',
      message:
        "Retrieval day is typically the largest single cost — about 9 days away. Here's what to expect and how your spending compares to others at your clinic.",
    },
  },
  'fertility/doctors': {
    pageEyebrow: 'Fertility · My Doctors',
    pageTitle: 'Your care team',
    pageDescriptor: 'Appointments, saved doctors, and prep for your next visit.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'YOUR CARE TEAM · FERTILITY',
      title: 'Ohio Fertility Network · Dr. Kim.',
      subtitle: 'Next appointment: tomorrow · Monitoring scan · Bring your stim log',
      pills: [
        { label: 'Dr. Kim · Ohio Fertility', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Scan tomorrow · bring stim log' },
        { label: 'Cycle 2 · Antagonist' },
      ],
    },
    novaMessage: {
      context: 'appointment prep',
      message:
        "Your scan is tomorrow morning. On stim day 8 they'll check follicle sizes and estrogen. Here are the questions worth asking.",
    },
  },
  'fertility/appointment-prep': {
    pageEyebrow: 'Fertility · Appointment Prep',
    pageTitle: 'Walk in prepared',
    pageDescriptor: 'Nova builds your cycle summary and question list before every clinic visit.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'APPOINTMENT PREP · FERTILITY',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Monitoring scan tomorrow', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Water bottle · ID · insurance' },
        { label: 'Cycle 2 · Antagonist' },
      ],
    },
    novaMessage: {
      context: 'clinic visit',
      message:
        'Your monitoring scan is tomorrow morning. Your bring list is packed — water bottle, photo ID, and insurance card. Want a few questions to ask at the desk?',
    },
  },
  'fertility/when-to-see-a-doctor': {
    pageEyebrow: 'FERTILITY · YOUR CYCLE GUIDE',
    pageTitle: 'What to expect today',
    pageDescriptor:
      "What's happening in your body each day of stimulation, what's normal to feel, and signs worth noting.",
    hideNovaMessage: true,
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'FERTILITY · YOUR CYCLE GUIDE',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Stim day 8 · active cycle', dot: true, dotColor: 'bg-rose-400' },
        { label: 'AFC 12 · watch zone' },
        { label: 'Cycle 2 · Antagonist' },
      ],
    },
    novaMessage: {
      context: 'guidance',
      message: 'Educational timelines only — your clinician knows your full history.',
    },
  },
  'fertility/medications': {
    pageEyebrow: 'Fertility · Medications',
    pageTitle: 'Your medications',
    pageDescriptor: "Tonight's injections, your full protocol, and Nova reminders.",
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'MEDICATIONS · IVF CYCLE 2 · STIM DAY 8',
      title: "Tonight's injections at 9pm.",
      subtitle: 'Gonal-F 150IU · Menopur 75IU · Cetrotide 0.25mg',
      pills: [
        { label: '3 medications tonight', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Reminder set · 9pm' },
        { label: 'Stim day 8 · Gonal-F' },
      ],
    },
    novaMessage: {
      context: 'medication reminder',
      message:
        'All three injections are at 9pm tonight. Cetrotide goes in the abdomen — Gonal-F and Menopur can alternate sites. Need a refresher on technique?',
    },
  },
  'fertility/community': {
    pageEyebrow: 'Fertility · Community',
    pageTitle: 'Women who get it',
    pageDescriptor: 'Matched to your cycle, your stage, and your situation — not a generic forum.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'IVF · CYCLE 2 · STIM DAY 8',
      title: 'Your fertility community.',
      subtitle: '7 women on stim day 8 in Stims Support — Jasmine M. is your closest match',
      pills: [
        { label: 'Stims support · 7 online', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Retrieval recovery live' },
        { label: 'Matched · Cycle 2 · Columbus' },
      ],
    },
    novaMessage: {
      context: 'community guide',
      message: '7 women on stim day 8 are in Stims Support right now — Jasmine M. is your closest match.',
    },
  },
  'fertility/connect': {
    pageEyebrow: 'Fertility · Connect',
    pageTitle: 'Women like you',
    pageDescriptor: 'Women Nova matched to you based on your timeline and situation.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'IVF · CYCLE 2 · STIM DAY 8',
      title: 'Women like you, matched by Nova.',
      subtitle: 'Same stage · Columbus · Jasmine M. is online right now',
      pills: [
        { label: 'Stim day 8 · Cycle 2', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Jamie matched this week' },
        { label: 'Retrieval same week as you' },
      ],
    },
    novaMessage: {
      context: 'matching now',
      message: 'Nova matched you with 3 women at the same stage in Columbus this week. Jasmine M. is online right now.',
    },
  },
  'fertility/lgbtq': {
    pageEyebrow: 'Fertility · LGBTQ+',
    pageTitle: 'Your path to parenthood',
    pageDescriptor: 'Your identity and your journey both belong here — Nova guides every step.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'LGBTQ+ · FERTILITY',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Reciprocal IVF · Cycle 1', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Partner 1 — stim day 8' },
        { label: 'Partner 2 — sync cycle next' },
      ],
    },
    novaMessage: {
      context: 'inclusive support',
      message: 'Your identity and your path to parenthood both belong here — Nova can help you find community and language that fits.',
    },
  },
  'fertility/partner-support': {
    pageEyebrow: 'Fertility · Partner Support',
    pageTitle: 'Support for your partner',
    pageDescriptor: 'How to talk about IVF, what they can do, and how Nova can help both of you.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'PARTNER SUPPORT',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Stim day 8', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Injection reminder tonight' },
        { label: 'Retrieval in ~9 days' },
      ],
    },
    novaMessage: {
      context: 'partner',
      message: 'IVF is a lot for partners too — when this space launches, you will find ways to stay connected and share the load.',
    },
  },
  'fertility/loss': {
    pageEyebrow: 'Fertility · Loss & Recovery',
    pageTitle: "You don't have to do this alone",
    pageDescriptor: 'After a loss — what to do medically, emotionally, and what comes next.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'LOSS & RECOVERY',
      title: '',
      subtitle: '',
      pills: [
        { label: 'You are not alone', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Nova is here' },
        { label: '847 women in this chapter' },
      ],
    },
    hideNovaMessage: true,
  },
  'fertility/events': {
    pageEyebrow: 'Fertility · Events Near Me',
    pageTitle: 'Events Near Me',
    pageDescriptor: 'Local meetups and support circles near you.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'IVF · CYCLE 2 · STIM DAY 8',
      title: 'Events near you.',
      subtitle: 'IVF Support Circle in Columbus, Ohio this Saturday — 14 women from your chapter are going',
      pills: [
        { label: 'Columbus circle · Saturday', dot: true, dotColor: 'bg-rose-400' },
        { label: '14 chapter women RSVP' },
        { label: 'Cycle 2 · stims' },
      ],
    },
    novaMessage: {
      context: 'local events',
      message: 'IVF Support Circle in Columbus, Ohio this Saturday — 14 women from your chapter are going.',
    },
  },
  'fertility/products': {
    pageEyebrow: 'Fertility · Products',
    pageTitle: 'Products',
    pageDescriptor: 'Curated picks matched to your cycle phase.',
    statusPillText: FERTILITY_DEMO_STATUS_PILL_TEXT,
    statusCard: {
      eyebrow: 'IVF · CYCLE 2 · STIM DAY 8',
      title: "Nova's picks for stim day 8.",
      subtitle: '200+ fertility products — curated and matched to your cycle stage',
      pills: [
        { label: 'Stim day 8 picks ready', dot: true, dotColor: 'bg-rose-400' },
        { label: 'Matched to protocol' },
        { label: 'Cycle 2 essentials' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "Based on your cycle, these are the products women like you reach for most during the stim phase.",
    },
  },

  'ttc/home': {
    pageEyebrow: 'TTC · Home',
    pageTitle: 'Your TTC hub',
    pageDescriptor: 'Cycle tracking, fertile window, and Nova — all in one place.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · MONTH 4 · FERTILE WINDOW',
      title: 'Your best window is open, Maya.',
      subtitle: "Month 4 — you've tracked every cycle, shown up every day. Today and tomorrow are peak.",
      pills: [
        { label: 'Peak fertile window', dot: true, dotColor: 'bg-orange-400' },
        { label: 'OPK: log now' },
        { label: '4 months trying' },
      ],
    },
    novaMessage: {
      context: 'fertile window check-in',
      message: 'Today is peak fertile window — cycle day 14. Did you test with an OPK this morning? I can help you read the result.',
    },
  },
  'ttc/myjourney': {
    pageEyebrow: 'TTC · My Journey',
    pageTitle: 'My Journey',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · MONTH 4 · NATURAL CYCLE',
      title: 'Ovulation window open.',
      subtitle: 'Mar 23, 2026 · Tracking with OPKs · Peak fertile day',
      pills: [
        { label: 'Day 14 · ovulatory', dot: true, dotColor: 'bg-orange-400' },
        { label: 'OPK peak · BBT shift confirmed' },
        { label: 'Fertile window day 2 of 3' },
      ],
      stat: { value: '2', label: 'day window' },
    },
    novaMessage: {
      context: 'midday check-in',
      message: 'Your OPK turned positive this morning — today and tomorrow are your best days. Have you logged your temp?',
    },
  },
  'ttc/opklog': {
    pageEyebrow: 'TTC · OPK Log',
    pageTitle: 'OPK Log',
    pageDescriptor: "Log and read OPKs with Nova's timing guidance.",
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'OPK LOG · CYCLE 4 · DAY 14',
      title: 'Positive OPK this morning.',
      subtitle: 'Peak day confirmed · Best timing: today and tomorrow · Log your result below',
      pills: [
        { label: 'LH surge · today', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Log result · peak day' },
        { label: '4 months tracking' },
      ],
    },
    novaMessage: {
      context: 'OPK reading',
      message:
        'A positive OPK means LH has surged — ovulation typically happens 12–36 hours later. Today and tomorrow are your best days.',
    },
  },
  'ttc/tww': {
    pageEyebrow: 'TTC · Two-Week Wait',
    pageTitle: 'Two-Week Wait',
    pageDescriptor: 'What to expect before and during the two-week wait.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TWW · NOT YET STARTED',
      title: 'TWW starts after ovulation.',
      subtitle: 'Fertile window open now · TWW begins ~day 16 · Test date: ~Apr 6',
      pills: [
        { label: 'Fertile window open', dot: true, dotColor: 'bg-orange-400' },
        { label: 'TWW starts ~day 16' },
        { label: 'Test date · ~Apr 6' },
      ],
    },
    novaMessage: {
      context: 'TWW guide',
      message:
        "The two-week wait starts after ovulation — roughly day 16. I know the waiting is hard. Here's what's worth knowing vs what's just noise.",
    },
  },
  'ttc/health': {
    pageEyebrow: 'TTC · Health Record',
    pageTitle: 'Your health at a glance',
    pageDescriptor: 'Labs, cycle history, and condition flags — all in one editable record.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'HEALTH RECORD · TTC',
      title: 'Your fertility health overview.',
      subtitle: 'Labs, cycle history, and doctor notes — Nova uses this to personalize your support',
      pills: [
        { label: 'Day 14 · Cycle 4', dot: true, dotColor: 'bg-orange-400' },
        { label: 'LH rising · log OPK now' },
        { label: '4 months tracking' },
      ],
    },
    novaMessage: {
      context: 'health snapshot',
      message:
        "Your cycle length is consistent at 28 days — that's a strong foundation. Adding your BBT and AMH here helps me give you better guidance.",
    },
  },
  'ttc/doctor': {
    pageEyebrow: 'TTC · My Doctor',
    pageTitle: 'My Doctor',
    pageDescriptor: 'Prep for specialist visits and when to escalate care.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · MY DOCTOR',
      title: '',
      subtitle: '',
      pills: [
        { label: 'No RE yet · 4 months', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Workup at 12 months' },
        { label: 'OB on file · Columbus' },
      ],
    },
    novaMessage: {
      context: 'specialist guidance',
      message:
        "You're in month 4 — most guidelines suggest seeing a specialist after 6–12 months depending on age. I can help you prepare that conversation now.",
    },
  },
  'ttc/community': {
    pageEyebrow: 'TTC · Community',
    pageTitle: 'Your TTC community',
    pageDescriptor: 'Matched to your cycle day, your stage, and your exact situation.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'CYCLE DAY 14 · TTC · FERTILE WINDOW',
      title: 'Your TTC community.',
      subtitle: 'Month 4 — 94 women online · Fertile Window room active right now',
      pills: [
        { label: 'Fertile window · 12 online', dot: true, dotColor: 'bg-orange-400' },
        { label: 'OPK reading room live' },
        { label: 'Matched · Cycle 4 · Columbus' },
      ],
    },
    novaMessage: {
      context: 'community update',
      message: '12 women in the TWW right now. The Fertile Window room is active — 94 online.',
    },
  },
  'ttc/connect': {
    pageEyebrow: 'TTC · Connect',
    pageTitle: 'Women like you',
    pageDescriptor: 'Women Nova matched to you based on your timeline and cycle.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'CYCLE DAY 14 · TTC · FERTILE WINDOW',
      title: 'Women like you, matched by Nova.',
      subtitle: 'Month 4 · Same cycle day · Columbus, OH',
      pills: [
        { label: 'Day 14 · Cycle 4', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Riley matched this week' },
        { label: 'Same OPK timing as you' },
      ],
    },
    novaMessage: {
      context: 'your matches',
      message: 'Nova matched you with Sarah K. — also cycle day 14 with a peak OPK today in Austin.',
    },
  },
  'ttc/events': {
    pageEyebrow: 'TTC · Events Near Me',
    pageTitle: 'Events Near Me',
    pageDescriptor: 'In-person support and circles in your area.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · MONTH 4 · FERTILE WINDOW',
      title: 'Events near you.',
      subtitle: 'TTC Support Circle in Columbus this weekend — 6 women from your chapter registered',
      pills: [
        { label: 'Columbus circle · weekend', dot: true, dotColor: 'bg-orange-400' },
        { label: '6 chapter women RSVP' },
        { label: 'Month 4 · peak timing' },
      ],
    },
    novaMessage: {
      context: 'local support',
      message: 'TTC Support Circle in Columbus this weekend — 6 women from your chapter registered.',
    },
  },

  'ttc/cycle': {
    pageEyebrow: 'TTC · My Cycle',
    pageTitle: 'My Cycle',
    pageDescriptor: "Today's log, BBT chart, OPK results, and cycle pattern insights.",
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · CYCLE TRACKING',
      title: 'Your cycle at a glance.',
      subtitle: 'Log days, symptoms, and timing — Nova uses this to personalize check-ins',
      pills: [
        { label: 'Day 14 · ovulatory', dot: true, dotColor: 'bg-orange-400' },
        { label: 'OPK peak · BBT shift confirmed' },
        { label: 'Fertile window day 2 of 3' },
      ],
    },
    novaMessage: {
      context: 'cycle check-in',
      message: 'Want help logging today or reading your fertile window?',
    },
  },
  'ttc/cycle-intelligence': {
    pageEyebrow: 'TTC · Cycle Intelligence',
    pageTitle: 'What your patterns reveal',
    pageDescriptor: 'Nova reads your cycle data across months and surfaces what is worth knowing.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'CYCLE INTELLIGENCE',
      title: '',
      subtitle: '',
      pills: [
        { label: '3 patterns detected', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Luteal phase flagged' },
        { label: '4 cycles of data' },
      ],
    },
    novaMessage: {
      context: 'insights preview',
      message: 'When this launches, Nova will surface patterns across your cycles — nothing to action yet.',
    },
  },
  'ttc/cycle-school': {
    pageEyebrow: 'TTC · Cycle School',
    pageTitle: 'Learn what your cycle means',
    pageDescriptor: 'Interactive explainers tied to your cycle day — not a generic article library.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'CYCLE SCHOOL',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Day 14 · ovulatory', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Today: LH surge explainer' },
        { label: '4 articles completed' },
      ],
    },
    novaMessage: {
      context: 'learning',
      message: 'Short lessons on hormones, timing, and what your body is doing — coming soon.',
    },
  },
  'ttc/appointment-prep': {
    pageEyebrow: 'TTC · Appointment Prep',
    pageTitle: 'Walk in prepared',
    pageDescriptor: 'Nova builds your cycle summary and question list before every appointment.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'APPOINTMENT PREP · TTC',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Next appt: OB · May 2026', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Question list ready' },
        { label: '4 cycles of data to share' },
      ],
    },
    novaMessage: {
      context: 'clinic visit',
      message: 'Question lists and what to bring — we are finishing this for you.',
    },
  },
  'ttc/when-to-see-a-doctor': {
    pageEyebrow: 'TTC · What to Expect',
    pageTitle: 'Know when to escalate',
    pageDescriptor: 'How long to try naturally based on your age — and what to expect at a workup.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'WHAT TO EXPECT',
      title: '',
      subtitle: '',
      pills: [
        { label: '4 months trying · under 35', dot: true, dotColor: 'bg-orange-400' },
        { label: '8 months until workup' },
        { label: 'Short luteal phase flagged' },
      ],
    },
    novaMessage: {
      context: 'guidance',
      message: 'Educational timelines only — your clinician knows your full history.',
    },
  },
  'ttc/partner-support': {
    pageEyebrow: 'TTC · Partner Support',
    pageTitle: 'Bring your partner in',
    pageDescriptor: 'How to talk about TTC stress, timing pressure, and when to consider joint testing.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'PARTNER SUPPORT · TTC',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Fertile window open', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Timing guide ready' },
        { label: '4 months trying together' },
      ],
    },
    novaMessage: {
      context: 'partner',
      message: 'Ways to navigate TTC together — this space is on the way.',
    },
  },
  'ttc/loss-and-recovery': {
    pageEyebrow: 'TTC · Loss & Recovery',
    pageTitle: "You don't have to do this alone",
    pageDescriptor: 'After a loss — what to do medically, emotionally, and what comes next.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'LOSS & RECOVERY · TTC',
      title: '',
      subtitle: '',
      pills: [
        { label: 'You are not alone', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Nova is here' },
        { label: '620 women in this chapter' },
      ],
    },
    novaMessage: {
      context: 'gentle support',
      message: 'You do not have to carry this alone — Nova is here when you want to talk.',
    },
  },
  'ttc/doctors': {
    pageEyebrow: 'TTC · My Doctors',
    pageTitle: 'Your care team',
    pageDescriptor: 'OB, RE, and specialist matching — by your situation, location, and insurance.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'MY DOCTORS · TTC',
      title: '',
      subtitle: '',
      pills: [
        { label: 'No RE yet · 4 months', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Workup at 12 months' },
        { label: 'OB on file · Columbus' },
      ],
    },
    novaMessage: {
      context: 'care team',
      message: 'Save your OB, RE, or GP here so Nova can tailor reminders and prep.',
    },
  },
  'ttc/resources': {
    pageEyebrow: 'TTC · Resources',
    pageTitle: 'Resources',
    pageDescriptor: 'Articles and trusted guides for this chapter.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'RESOURCES · TTC',
      title: '',
      subtitle: '',
      pills: [
        { label: 'Day 14 · guides open', dot: true, dotColor: 'bg-orange-400' },
        { label: 'Save articles · offline' },
        { label: '4 months on this path' },
      ],
    },
    novaMessage: {
      context: 'reading list',
      message: 'Trusted articles and guides — curated for where you are in TTC.',
    },
  },

  'pregnancy/home': {
    pageTitle: 'Your Pregnancy Hub',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · SECOND TRIMESTER',
      title: 'Halfway there, Maya.',
      subtitle: 'Anatomy scan this week · Baby moving more · Glucose test in 4 weeks',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Anatomy scan: Apr 6' },
        { label: '140 days to go' },
      ],
    },
    novaMessage: {
      context: 'morning check-in',
      message: "Week 20 is a milestone — you're halfway! Your anatomy scan is Apr 6. Any questions about what they'll check?",
    },
  },
  'pregnancy/weekbyweek': {
    pageTitle: 'Week by Week',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · DAY 3',
      title: 'Second trimester, week 20.',
      subtitle: 'Mar 23, 2026 · OB at Cleveland Clinic · Anatomy scan Apr 6',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Anatomy scan: Apr 6' },
        { label: '140 days to go' },
      ],
      stat: { value: '26', label: 'weeks to go' },
    },
    novaMessage: {
      context: 'week 20 check-in',
      message:
        "You're halfway. Your anatomy scan is Apr 6 — here's exactly what they check and what questions are worth asking.",
    },
  },
  'pregnancy/health': {
    pageTitle: 'Health Record',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'HEALTH RECORD · PREGNANCY · WEEK 20',
      title: 'Your pregnancy health overview.',
      subtitle: 'Labs, ultrasounds, and appointments — all in one place',
      pills: [
        { label: 'Week 20', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Next: Anatomy scan Apr 6' },
      ],
    },
    novaMessage: {
      context: 'health update',
      message:
        "Your glucose tolerance test is coming up around week 24–28. I'll remind you — and help you prepare for what it involves.",
    },
  },
  'pregnancy/birthplan': {
    pageTitle: 'Birth Plan',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'BIRTH PLAN · WEEK 20',
      title: 'Start building your birth plan.',
      subtitle: 'Week 20 is the right time to start — anatomy scan will inform some decisions',
      pills: [
        { label: 'Not started', dot: true, dotColor: 'bg-gray-300' },
        { label: 'Anatomy scan first' },
      ],
    },
    novaMessage: {
      context: 'birth plan guide',
      message:
        'Birth plans work best when you understand your actual options. I can walk you through each decision point — starting with what your anatomy scan might tell you.',
    },
  },
  'pregnancy/leave': {
    pageTitle: 'Preparing for Leave',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'LEAVE PLANNING · WEEK 20',
      title: 'Leave starts around week 36.',
      subtitle: '16 weeks to plan · Some policies require notice 8+ weeks in advance',
      pills: [
        { label: '~16 weeks to plan', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Notice deadline varies' },
      ],
    },
    novaMessage: {
      context: 'leave planning',
      message:
        'Week 20 is a good time to start thinking about leave — policies vary and some require notice well in advance. I can help you figure out what applies.',
    },
  },
  'pregnancy/careteam': {
    pageTitle: 'My Care Team',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'CARE TEAM · PREGNANCY',
      title: 'OB at Cleveland Clinic.',
      subtitle: 'Next appointment: Apr 6 · Anatomy scan · Dr. Chen',
      pills: [
        { label: 'Anatomy scan Apr 6', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Cleveland Clinic' },
      ],
    },
    novaMessage: {
      context: 'appointment prep',
      message:
        'Your anatomy scan is Apr 6. I can help you prep a question list — week 20 scans cover a lot and it helps to know what to look for.',
    },
  },
  'pregnancy/medications': {
    pageTitle: 'Medications',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'MEDICATIONS · PREGNANCY · WEEK 20',
      title: 'Prenatal vitamins and supplements.',
      subtitle: 'All current medications and supplements logged here',
      pills: [
        { label: 'Prenatal: daily', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'DHA supplement' },
      ],
    },
    novaMessage: {
      context: 'medication check',
      message:
        "If you're unsure whether something is safe to take during pregnancy — ask me before taking it. Not everything labeled 'natural' is safe.",
    },
  },
  'pregnancy/community': {
    pageTitle: 'Community',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · SECOND TRIMESTER',
      title: 'Your pregnancy community.',
      subtitle: '14 women having anatomy scans this week — Week 20 room is active',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Anatomy scan: Apr 6' },
        { label: 'Second trimester' },
      ],
    },
    novaMessage: {
      context: 'community update',
      message: '14 women having anatomy scans this week — Week 20 room is active right now.',
    },
  },
  'pregnancy/connect': {
    pageTitle: 'Connect',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · SECOND TRIMESTER',
      title: 'Women like you, matched by Nova.',
      subtitle: 'Ava K. also week 20 with anatomy scan this week in Cincinnati',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Same week' },
        { label: 'Second trimester' },
      ],
    },
    novaMessage: {
      context: 'your matches',
      message: 'Ava K. is also week 20 with an anatomy scan this week in Cincinnati — your closest match.',
    },
  },
  'pregnancy/loss': {
    pageTitle: 'Loss & Support',
    statusPillText: 'Week 20 · Second trimester',
    novaMessage: {
      context: 'here with you',
      message: 'Pregnancy after loss or a high-risk diagnosis brings its own kind of anxiety. This space holds all of it.',
    },
  },
  'pregnancy/events': {
    pageTitle: 'Events Near Me',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · SECOND TRIMESTER',
      title: 'Events near you.',
      subtitle: 'Pregnancy After Infertility Circle in Columbus — Apr 12 · 8 women registered',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Columbus' },
        { label: 'Anatomy scan Apr 6' },
      ],
    },
    novaMessage: {
      context: 'local events',
      message: 'Pregnancy After Infertility Circle in Columbus — Apr 12. 8 women from your chapter registered.',
    },
  },

  'motherhood/home': {
    pageTitle: 'Your Motherhood Hub',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: 'Week 6 with Sofia, Maya.',
      subtitle: "6-week check-up this week · PPD screening · You're doing amazing",
      pills: [
        { label: '6 weeks old', dot: true, dotColor: 'bg-violet-400' },
        { label: '6-week check-up due' },
        { label: 'PPD screening today' },
      ],
    },
    novaMessage: {
      context: 'week 6 check-in',
      message: 'Sofia is 6 weeks old — huge milestone. Your check-up this week covers physical healing, contraception, and mood. How are you really doing — not just the baby?',
    },
  },
  'motherhood/mybaby': {
    pageTitle: 'My Baby',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: 'Sofia is 6 weeks old today. 🤱',
      subtitle: 'Born Feb 9, 2026 · 6-week check-up this week · PPD screening due',
      pills: [
        { label: '6 weeks old', dot: true, dotColor: 'bg-violet-400' },
        { label: '6-week check-up due' },
        { label: 'PPD screening today' },
      ],
      stat: { value: '42', label: 'days old' },
    },
    novaMessage: {
      context: 'week 6 check-in',
      message:
        'Sofia is 6 weeks — a huge milestone. Your check-up this week covers your healing, mood, and contraception. How are YOU doing — not just the baby?',
    },
  },
  'motherhood/myjourney': {
    pageTitle: 'My Journey',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6',
      title: 'Week 6 with Sofia.',
      subtitle: 'Born Feb 9, 2026 · 6-week check-up due · PPD screening today',
      pills: [
        { label: 'Week 6 postpartum', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Check-up this week' },
      ],
      stat: { value: '42', label: 'days old' },
    },
    novaMessage: {
      context: 'week 6 check-in',
      message:
        "Your 6-week check-up covers physical healing, PPD screening, and contraception. It's okay to have a lot to say.",
    },
  },
  'motherhood/milestones': {
    pageTitle: 'Milestone Support',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'MILESTONES · WEEK 6',
      title: 'Coming up: first social smile.',
      subtitle: '6–8 weeks is when most babies smile socially for the first time',
      pills: [
        { label: 'Week 6', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Social smile: any day' },
      ],
    },
    novaMessage: {
      context: 'milestone watch',
      message: "Social smiles typically appear between 6–8 weeks. It's one of those moments that makes the exhaustion worth it.",
    },
  },
  'motherhood/wellbeing': {
    pageTitle: 'My Wellbeing',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'YOUR WELLBEING · WEEK 6',
      title: 'PPD screening is due this week.',
      subtitle: 'Your 6-week check-up includes a mood and mental health assessment',
      pills: [
        { label: 'Screening due', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Mood: not logged today' },
      ],
    },
    novaMessage: {
      context: 'PPD check-in',
      message:
        "PPD and postpartum anxiety affect 1 in 5 new mothers — and it's not always obvious. How have you been feeling this week, really?",
    },
  },
  'motherhood/working': {
    pageTitle: 'Working Mother',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    novaMessage: {
      context: 'return to work',
      message:
        "Week 6 is when a lot of return-to-work decisions become real. Whether you're going back soon or not — I can help you think through what you need.",
    },
  },
  'motherhood/activities': {
    pageTitle: 'Activities & Camps',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    novaMessage: {
      context: 'local activities',
      message: 'New parent groups and postpartum yoga in Columbus — good for week 6+ when you\'re cleared for gentle activity.',
    },
  },
  'motherhood/health': {
    pageTitle: 'Health Record',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    novaMessage: {
      context: 'health record',
      message:
        "Your postpartum recovery and Sofia's growth records are here. Your OB and pediatrician can both access what you choose to share.",
    },
  },
  'motherhood/careteam': {
    pageTitle: 'My Care Team',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    novaMessage: {
      context: 'care team',
      message: "Your 6-week check-up is this week. Sofia's 2-month visit with Dr. Patel is Apr 9.",
    },
  },
  'motherhood/community': {
    pageTitle: 'Community',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: 'Your motherhood community.',
      subtitle: '106 women online · Fourth Trimester room has 22 sharing right now',
      pills: [
        { label: '6 weeks postpartum', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Fourth trimester' },
        { label: 'Your people' },
      ],
    },
    novaMessage: {
      context: 'community update',
      message: '106 women online. Fourth Trimester room has 22 women sharing right now — postpartum weeks 4–8. Your people.',
    },
  },
  'motherhood/connect': {
    pageTitle: 'Connect',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: 'Moms like you, matched by Nova.',
      subtitle: "Jasmine T. is also 6 weeks postpartum in Columbus — Nova's closest match",
      pills: [
        { label: '6 weeks postpartum', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Same baby age' },
        { label: 'Columbus' },
      ],
    },
    novaMessage: {
      context: 'your matches',
      message: "Jasmine T. is also 6 weeks postpartum in Columbus — Nova's closest match for you right now.",
    },
  },
  'motherhood/ppd': {
    pageTitle: 'PPD Support',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    novaMessage: {
      context: 'PPD support',
      message: "This space is specifically for postpartum mood. You don't have to be in crisis to be here — struggling is enough.",
    },
  },
  'motherhood/events': {
    pageTitle: 'Events Near Me',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: 'Events near you.',
      subtitle: 'Postpartum Moms Meetup in Columbus this Saturday — stroller-friendly, babies welcome',
      pills: [
        { label: '6 weeks postpartum', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Columbus' },
        { label: 'Fourth trimester' },
      ],
    },
    novaMessage: {
      context: 'local events',
      message: 'Postpartum Moms Meetup in Columbus this Saturday — stroller-friendly, babies welcome.',
    },
  },

  'menopause/home': {
    pageTitle: 'Your Menopause Hub',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · MONTH 3 OF TRACKING',
      title: 'Nova has been watching your patterns, Riley.',
      subtitle: '14 days logged · 3 symptoms tracked · Next OB appointment Apr 8',
      pills: [
        { label: 'Perimenopause', dot: true, dotColor: 'bg-pink-400' },
        { label: '14 days tracked' },
        { label: 'Appointment Apr 8' },
      ],
    },
    novaMessage: {
      context: 'pattern update',
      message: "Based on your last 14 days, your hot flashes peak between 6–8pm. I've prepared a symptom summary for Dr. Chang on Apr 8.",
    },
  },
  'menopause/myjourney': {
    pageTitle: 'My Journey',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · MONTH 3 OF TRACKING',
      title: 'Nova has been watching your patterns.',
      subtitle: '14 days logged · 3 symptoms tracked daily · Next OB appointment is April 8',
      pills: [
        { label: 'Perimenopause', dot: true, dotColor: 'bg-pink-400' },
        { label: '14 days tracked' },
        { label: 'Appointment Apr 8' },
      ],
    },
    novaMessage: {
      context: 'pattern update',
      message:
        "Based on your last 14 days, your hot flashes peak between 6–8pm and your sleep disruption correlates with them. I've prepared a symptom summary for Dr. Chang.",
    },
  },
  'menopause/symptoms': {
    pageTitle: 'Symptom Tracker',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'SYMPTOM TRACKER · PERIMENOPAUSE',
      title: '14 days of patterns logged.',
      subtitle: 'Hot flashes peak 6–8pm · Sleep disruption correlates · Log today below',
      pills: [
        { label: '14 days tracked', dot: true, dotColor: 'bg-pink-400' },
        { label: '3 symptoms daily' },
      ],
    },
    novaMessage: {
      context: 'pattern update',
      message:
        "Your hot flashes consistently peak between 6–8pm. This pattern is useful data for Dr. Chang — I've prepared a summary you can bring.",
    },
  },
  'menopause/hrt': {
    pageTitle: 'HRT & Medications',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'HRT & MEDICATIONS · PERIMENOPAUSE',
      title: 'HRT: not yet started.',
      subtitle: 'Dr. Chang appointment Apr 8 — HRT discussion on the agenda',
      pills: [
        { label: 'HRT: not started', dot: true, dotColor: 'bg-gray-300' },
        { label: 'Discussing Apr 8' },
      ],
    },
    novaMessage: {
      context: 'HRT guide',
      message:
        'HRT decisions deserve real information, not fear. The 2024 guidelines are very different from the messaging of the past. I can walk you through the evidence.',
    },
  },
  'menopause/health': {
    pageTitle: 'Health Record',
    statusPillText: 'Tracking symptoms · 14 days logged',
    novaMessage: {
      context: 'health record',
      message:
        'Your symptom log and lab results are here. Adding your FSH and estradiol levels helps me give you more specific guidance.',
    },
  },
  'menopause/doctorprep': {
    pageTitle: 'Doctor Prep',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'DR. CHANG APPOINTMENT · APR 8',
      title: 'Appointment prep ready.',
      subtitle: '14 days of symptom data · Pattern summary · Question list — ready to share',
      pills: [
        { label: '16 days away', dot: true, dotColor: 'bg-pink-400' },
        { label: 'Summary ready' },
      ],
    },
    novaMessage: {
      context: 'appointment prep',
      message:
        "I've prepared your symptom summary for Dr. Chang — hot flash pattern, sleep disruption data, and a question list. Ready to download.",
    },
  },
  'menopause/specialist': {
    pageTitle: 'My Specialist',
    statusPillText: 'Tracking symptoms · 14 days logged',
    novaMessage: {
      context: 'care team',
      message: "Dr. Chang at Northwestern Medicine. Apr 8 appointment confirmed — I've already prepared materials you can bring.",
    },
  },
  'menopause/community': {
    pageTitle: 'Community',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · 14 DAYS TRACKED',
      title: 'Your menopause community.',
      subtitle: "Hot flash management room active — 16 women comparing what's actually working",
      pills: [
        { label: '14 days tracked', dot: true, dotColor: 'bg-pink-400' },
        { label: '3 symptoms logged' },
        { label: 'HRT discussion Apr 8' },
      ],
    },
    novaMessage: {
      context: 'community update',
      message: "Hot flash management room active — 16 women comparing notes on what's actually working.",
    },
  },
  'menopause/connect': {
    pageTitle: 'Connect',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · 14 DAYS TRACKED',
      title: 'Women like you, matched by Nova.',
      subtitle: 'Linda K. also perimenopause month 5 in Denver — same symptom patterns',
      pills: [
        { label: '14 days tracked', dot: true, dotColor: 'bg-pink-400' },
        { label: 'Same stage' },
        { label: 'Tracking symptoms' },
      ],
    },
    novaMessage: {
      context: 'your matches',
      message: 'Linda K. is also perimenopause month 5 in Denver — tracking the same symptom patterns as you.',
    },
  },
  'menopause/events': {
    pageTitle: 'Events Near Me',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · 14 DAYS TRACKED',
      title: 'Events near you.',
      subtitle: 'Menopause & HRT Q&A webinar this Wednesday — free, hosted by The Menopause Society',
      pills: [
        { label: '14 days tracked', dot: true, dotColor: 'bg-pink-400' },
        { label: 'HRT discussion Apr 8' },
        { label: 'Virtual options' },
      ],
    },
    novaMessage: {
      context: 'local events',
      message: 'Menopause & HRT Q&A webinar this Wednesday — free, hosted by The Menopause Society.',
    },
  },
  'menopause/products': {
    pageTitle: 'Products',
    statusPillText: 'Tracking symptoms · 14 days logged',
    statusCard: {
      eyebrow: 'PERIMENOPAUSE · 14 DAYS TRACKED',
      title: "Nova's picks for menopause.",
      subtitle: 'Supplements, cooling products, and tools matched to your symptoms',
      pills: [
        { label: '14 days tracked', dot: true, dotColor: 'bg-pink-400' },
        { label: '3 symptoms logged' },
        { label: 'Hot flash support' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "These are the products women like you use most for hot flashes and sleep — I can help you decide what's worth trying first.",
    },
  },

  'firstchapter/home': {
    pageTitle: 'Your First Chapter Hub',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: 'Day 14 of your cycle, Maya.',
      subtitle: 'Middle of your cycle · Energy tends to peak now · Log how you feel today',
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: '3-day mood streak' },
        { label: 'Regular cycle' },
      ],
    },
    novaMessage: {
      context: 'hey 👋',
      message: "You're on day 14 — roughly the middle of your cycle. Energy is usually highest now. How are you feeling today?",
    },
  },
  'firstchapter/myjourney': {
    pageTitle: 'My Journey',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: 'Day 14 of your cycle. 🌙',
      subtitle: 'Last period: Mar 9 · Cycle length: ~28 days · Next period: ~Apr 6',
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: '3-day mood streak' },
        { label: 'Regular cycle' },
      ],
    },
    novaMessage: {
      context: 'hey 👋',
      message:
        "You're on day 14 — roughly the middle of your cycle. Energy is usually highest now. How are you feeling today?",
    },
  },
  'firstchapter/tracker': {
    pageTitle: 'Cycle Tracker',
    statusPillText: 'Cycle day 14',
    hideStatusCard: true,
    hideNovaMessage: true,
  },
  'firstchapter/periodtracker': {
    pageTitle: 'Period Tracker',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'PERIOD TRACKER · CYCLE DAY 14',
      title: 'Day 14 — ovulation phase.',
      subtitle: 'Cycle length: 28 days · Next period: ~Apr 6 · 3-day mood streak',
      pills: [
        { label: 'Ovulation phase', dot: true, dotColor: 'bg-rose-300' },
        { label: 'Next period: Apr 6' },
      ],
    },
    novaMessage: {
      context: 'cycle update',
      message:
        "Day 14 is the ovulation phase in a 28-day cycle. Energy tends to peak now. I'll remind you when your period is coming.",
    },
  },
  'firstchapter/learn': {
    pageTitle: 'Learn About My Body',
    statusPillText: 'Cycle day 14',
    subtitle: 'Cycle day 14 · Ovulation phase',
    hideStatusCard: true,
    hideNovaMessage: true,
  },
  'firstchapter/mom': {
    pageTitle: 'My Chapter',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · SUPPORT',
      title: "It's not just you.",
      subtitle:
        "Six things happening to your body and mind right now that nobody properly explained. What they actually are, why they happen, and what actually helps — not what sounds helpful.",
      pills: [{ label: 'Cycle day 14' }, { label: 'Always private' }, { label: 'No wrong feelings' }],
    },
    novaMessage: {
      context: 'always here',
      message:
        "I know your cycle, your chapter, and what's been going on. Nothing is too much or too weird to bring here.",
    },
  },
  'firstchapter/doctor': {
    pageTitle: 'My Doctor',
    statusPillText: 'Cycle day 14',
    novaMessage: {
      context: 'doctor visit',
      message:
        "Not sure what to bring up at a doctor's appointment? I can help you figure out which questions are worth asking and how to say them.",
    },
  },
  'firstchapter/community': {
    pageTitle: 'Community',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: 'Your community.',
      subtitle: '9 women learning their cycle this week · Cycle tracking beginners room is active',
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: '3-day mood streak' },
        { label: 'Learning together' },
      ],
    },
    novaMessage: {
      context: 'community update',
      message: '9 women learning their cycle this week. Cycle tracking beginners room is active.',
    },
  },
  'firstchapter/connect': {
    pageTitle: 'Connect',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: 'People like you, matched by Nova.',
      subtitle: 'Emma T. also cycle day 14 — learning her cycle in Portland',
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: 'Same cycle day' },
        { label: 'Your closest match' },
      ],
    },
    novaMessage: {
      context: 'your matches',
      message: 'Emma T. is also cycle day 14 — learning her cycle in Portland. Your closest match right now.',
    },
  },
  'firstchapter/events': {
    pageTitle: 'Events Near Me',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: 'Events near you.',
      subtitle: "Teen & Young Women's Health Q&A — virtual, Apr 10 · Free, hosted by Planned Parenthood",
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: 'Virtual' },
        { label: 'Free' },
      ],
    },
    novaMessage: {
      context: 'local events',
      message: "Teen & Young Women's Health Q&A — virtual, Apr 10. Free, hosted by Planned Parenthood.",
    },
  },
  'firstchapter/products': {
    pageTitle: 'Products',
    statusPillText: 'Cycle day 14',
    statusCard: {
      eyebrow: 'FIRST CHAPTER · CYCLE DAY 14',
      title: "Nova's picks for cycle tracking.",
      subtitle: 'Period products, trackers, and essentials — curated for learning your cycle',
      pills: [
        { label: 'Cycle day 14', dot: true, dotColor: 'bg-rose-300' },
        { label: '3-day mood streak' },
        { label: 'Beginners' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "These are the products women use most when they're first learning their cycle — I can help you choose.",
    },
  },
  'ttc/products': {
    pageEyebrow: 'TTC · Products',
    pageTitle: 'Products',
    pageDescriptor: 'OPKs, supplements, and trackers matched to your cycle.',
    statusPillText: 'Cycle 4 · Day 14',
    statusCard: {
      eyebrow: 'TTC · MONTH 4 · FERTILE WINDOW',
      title: "Nova's picks for your fertile window.",
      subtitle: 'OPKs, supplements, and trackers — matched to where you are in your cycle',
      pills: [
        { label: 'Peak window picks ready', dot: true, dotColor: 'bg-orange-400' },
        { label: 'OPKs · BBT · supplements' },
        { label: 'Matched to fertile window' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "Based on cycle day 14 and your fertile window, these are the products women like you reach for most.",
    },
  },
  'pregnancy/products': {
    pageTitle: 'Products',
    statusPillText: 'Week 20 · Second trimester',
    statusCard: {
      eyebrow: 'PREGNANCY · WEEK 20 · SECOND TRIMESTER',
      title: "Nova's picks for pregnancy.",
      subtitle: 'Prenatals, belly support, and essentials — matched to your trimester',
      pills: [
        { label: '20 weeks', dot: true, dotColor: 'bg-emerald-400' },
        { label: 'Anatomy scan: Apr 6' },
        { label: 'Second trimester' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "Second trimester essentials — these are what women at 20 weeks reach for most.",
    },
  },
  'motherhood/products': {
    pageTitle: 'Products',
    statusPillText: 'Sofia · 6 weeks · Postpartum',
    statusCard: {
      eyebrow: 'POSTPARTUM · WEEK 6 · SOFIA',
      title: "Nova's picks for postpartum.",
      subtitle: 'Recovery, feeding, and baby essentials — matched to 6 weeks',
      pills: [
        { label: '6 weeks postpartum', dot: true, dotColor: 'bg-violet-400' },
        { label: 'Fourth trimester' },
        { label: 'Your people' },
      ],
    },
    novaMessage: {
      context: 'products',
      message: "Week 6 — these are the products new moms reach for most for recovery and baby.",
    },
  },
};

function normalizeChapter(chapter) {
  if (chapter === 'cycle' || chapter === 'first-chapter') return 'firstchapter';
  if (chapter === 'trying-to-conceive') return 'ttc';
  if (chapter === 'postpartum' || chapter === 'motherhood') return 'motherhood';
  return chapter || 'fertility';
}

/** Chapter-specific pill text fallback (used when no PAGE_HEADERS config exists) */
export function getChapterPillText(chapter) {
  const ch = normalizeChapter(chapter);
  const pills = {
    fertility: FERTILITY_DEMO_STATUS_PILL_TEXT,
    ttc: 'Cycle 4 · Day 14',
    pregnancy: 'Week 20 · Second trimester',
    motherhood: 'Sofia · 6 weeks · Postpartum',
    menopause: 'Tracking symptoms · 14 days logged',
    firstchapter: 'Cycle day 14',
  };
  return pills[ch] || pills.fertility;
}

function normalizeSection(chapter, section) {
  const s = section || 'home';
  const map = {
    journey: 'myjourney',
    baby: 'mybaby',
    doctor: chapter === 'pregnancy' ? 'careteam' : chapter === 'menopause' ? 'specialist' : chapter === 'motherhood' ? 'careteam' : 'doctor',
    doctors: 'doctors',
    lgbtq: 'lgbtq',
    'partner-support': 'partner-support',
    opk: 'opklog',
    myjourney: chapter === 'pregnancy' ? 'weekbyweek' : chapter === 'menopause' ? 'myjourney' : chapter === 'firstchapter' ? 'myjourney' : 'myjourney',
    health: chapter === 'firstchapter' ? 'periodtracker' : 'health',
    'loss-and-recovery': 'loss-and-recovery',
  };
  return map[s] || s;
}

export function getPageHeaderProps(chapter, section, navigate) {
  const ch = normalizeChapter(chapter);
  const sect = normalizeSection(ch, section);
  const key = `${ch}/${sect}`;
  const config = PAGE_HEADERS[key];
  if (!config) return null;

  const themeKey = ch === 'firstchapter' ? 'first-chapter' : ch;
  const theme = CHAPTER_THEMES[themeKey] || CHAPTER_THEMES.fertility;
  const demoName = 'Maya';
  const personalizeTitle = (title) => {
    const raw = String(title || '').trim();
    if (!raw) return raw;
    if (new RegExp(`\\b${demoName}\\b`, 'i').test(raw)) return raw;
    if (/^your\b/i.test(raw)) return `${demoName}, ${raw.charAt(0).toLowerCase()}${raw.slice(1)}`;
    return `${demoName}, ${raw.charAt(0).toLowerCase()}${raw.slice(1)}`;
  };
  /** Fertility + TTC: hide the two hero lines under the page title (personalized title + status subtitle); keep pills and pageDescriptor. */
  const stripStatusCardHeroCopy = ch === 'fertility' || ch === 'ttc';
  const personalizedStatusCard = config.statusCard
    ? {
        ...config.statusCard,
        ...(stripStatusCardHeroCopy
          ? { title: '', subtitle: '' }
          : { title: personalizeTitle(config.statusCard.title) }),
      }
    : undefined;

  const headerChapter = ch === 'ttc' || ch === 'firstchapter' ? 'ttc' : 'fertility';
  const shortLabel =
    ch === 'ttc' || ch === 'firstchapter'
      ? 'TTC'
      : CHAPTER_DISPLAY_NAMES[ch] === 'Trying to Conceive'
        ? 'TTC'
        : CHAPTER_DISPLAY_NAMES[ch] || 'Fertility';

  return {
    pageTitle: config.pageTitle,
    chapter: CHAPTER_DISPLAY_NAMES[ch] || 'Fertility',
    headerChapter,
    eyebrow: config.pageEyebrow ?? `${shortLabel} · ${config.pageTitle}`,
    theme,
    statusPillText: config.statusPillText,
    subtitle: config.pageDescriptor ?? config.subtitle,
    statusCard: personalizedStatusCard,
    novaMessage: config.novaMessage ?? undefined,
    onSignUp: navigate ? (p) => navigate(p || '/signup') : undefined,
    hideStatusCard: config.hideStatusCard ?? false,
    hideNovaMessage: config.hideNovaMessage ?? false,
  };
}
